import _extends from "@babel/runtime/helpers/extends";
/**
 * A higher-order function that provides filter fields for asset tags.
 * These filter fields are determined by fetching for tags/values given a set of asset types.
 * Each tag is represented as a distinct filter field with its own set of values.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { AssetType, ASSET_TAG_FILTER_GROUP } from '@kentik/ui-shared/labels/lib';
import BoardModel from 'app/stores/boards/BoardModel';
import DashboardModel from 'app/stores/dashboard/DashboardModel';
import SavedViewModel from 'app/stores/savedView/SavedView';
import { Select } from 'core/form';
function withAssetTagAdminFilters(assetTypes) {
  return WrappedComponent => inject('$assetTags')(observer(({
    $assetTags,
    ...props
  }) => {
    const {
      collection: adminTableCollection,
      filterValues
    } = props;
    const [loading, setLoading] = useState(false);

    /**
     * Fetches tag usage and values by asset types
     * These values are used to assemble options and default values
     */
    useEffect(() => {
      if (!$assetTags.canView) {
        return;
      }
      setLoading(true);
      $assetTags.collection.loadItems(assetTypes).then(() => {
        adminTableCollection.filter();
        setLoading(false);
      }).catch(() => setLoading(false));
    }, [adminTableCollection, $assetTags, $assetTags.canView]); // assetTypes is already a stable reference passed in the hof

    /**
     * Filter out models that match only the asset types we asked for
     */
    const models = useMemo(() => $assetTags.collection.get().filter(model => assetTypes.some(assetType => model.entityUsage.has(assetType))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // intent is to only run once
    );

    /**
     * Assembles filter fields based on asset types.
     * We always include the 'asset_tag' filter because it enables hashed filters to be picked up.
     * 'asset_tag' filters can be provided by clicking FilterLinks that create filters based on tag usage alone (not values).
     */
    const filters = useMemo(() => [{
      name: 'asset_tag',
      hidden: true
    }, ...models.map(model => ({
      label: model.keyName,
      placeholder: 'No values selected',
      allowMultiSelect: true,
      options: loading ? [] : $assetTags.collection.getTagFilterOptions({
        assetTypes,
        tagId: model.id
      }),
      name: model.filterName,
      group: ASSET_TAG_FILTER_GROUP,
      customField: /*#__PURE__*/React.createElement(Select, {
        multi: true,
        keepOpen: true,
        toggle: true,
        showClearButton: true,
        showFilter: true,
        showSelectAll: true
      }),
      filter: (filterModel, values) => {
        // most filters are a single entity so we default to that here
        let [assetType] = assetTypes;

        // library filters support dashboard, observation decks, and saved views
        // in those cases, we pass in all of those entities so we get a comprehensive list of options
        // we decide which filter to use against those three based on the type of the model being evaluated
        if (filterModel instanceof BoardModel) {
          assetType = AssetType.BOARD;
        } else if (filterModel instanceof DashboardModel) {
          assetType = AssetType.DASHBOARD;
        } else if (filterModel instanceof SavedViewModel) {
          assetType = AssetType.SAVED_VIEW;
        }
        return $assetTags.multiSelectModelFilter({
          relatedAssetModel: filterModel,
          assetType,
          tagId: model.id,
          values
        });
      }
    }))], [$assetTags, models, loading] // assetTypes is already a stable reference passed in the hof
    );

    /**
     * Processes filter values based on the possible presence of the 'asset_tag' filter.
     * If the 'asset_tag' filter is present, we will use that data to auto-populate individual tag filters with all their possible values.
     * Once we've found migrated the 'asset_tag' filter to the individual counterpart, the 'asset_tag' filter is reset.
     */
    const nextFilterValues = useMemo(() => {
      if (filterValues?.asset_tag && !loading) {
        // we currently auto-populate only one asset tag filter at a time (via FilterLink)
        const [tagId] = filterValues.asset_tag;

        // get the full model by id
        const assetTagModel = models.find(model => model.get('id') === tagId);
        if (assetTagModel) {
          // we found the model so we can populate the filter values for it then remove the asset_tag filter that got us here
          return {
            ...filterValues,
            asset_tag: undefined,
            [assetTagModel.filterName]: $assetTags.collection.getTagFilterValues({
              assetTypes,
              tagId: assetTagModel.id
            })
          };
        }
      }
      return filterValues;
    }, [filterValues, loading, models, $assetTags]); // assetTypes is already a stable reference passed in the hof

    if (!$assetTags.canView) {
      // skip asset tag filters and just pass through
      // @ts-expect-error complains about filterValues
      return /*#__PURE__*/React.createElement(WrappedComponent, _extends({}, props, {
        filterValues: filterValues,
        assetTagFilterFields: []
      }));
    }

    // @ts-expect-error complains about filterValues
    return /*#__PURE__*/React.createElement(WrappedComponent, _extends({}, props, {
      filterValues: nextFilterValues,
      assetTagFilterFields: filters
    }));
  }));
}
export default withAssetTagAdminFilters;